/* Pipeline 172X: brand identity components
   The 172 badge is the primary brand mark. It works at all sizes as:
   - App icon / favicon
   - Sidebar collapsed mark
   - Nav wordmark companion
   The badge never renders a separator; the structured wordmark carries the
   product identifier alongside it. */

import { BrandWordmark } from "./BrandWordmark";

interface BadgeProps {
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
}

const sizeMap = {
  xs: { outer: 16, font: 7, radius: 3, weight: 700 },
  sm: { outer: 22, font: 9, radius: 4, weight: 700 },
  md: { outer: 28, font: 11, radius: 5, weight: 700 },
  lg: { outer: 36, font: 14, radius: 6, weight: 800 },
  xl: { outer: 48, font: 19, radius: 8, weight: 800 },
};

export function Badge172({ size = "md", className = "" }: BadgeProps) {
  const s = sizeMap[size];
  return (
    <svg
      width={s.outer}
      height={s.outer}
      viewBox={`0 0 ${s.outer} ${s.outer}`}
      fill="none"
      role="img"
      aria-label="172X Pipeline"
      className={className}
    >
      <rect width={s.outer} height={s.outer} rx={s.radius} fill="#1a3a5c" />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fill="white"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize={s.font}
        fontWeight={s.weight}
        letterSpacing="-0.03em"
      >
        172
      </text>
    </svg>
  );
}

interface LogoProps {
  collapsed?: boolean;
  className?: string;
}

export function Logo({ collapsed = false, className = "" }: LogoProps) {
  return (
    <div
      className={`flex items-center gap-2.5 ${className}`}
      role="group"
      aria-label="172X Pipeline"
    >
      <Badge172 size="sm" />
      {!collapsed && (
        <div className="flex flex-col leading-none">
          <BrandWordmark
            className="font-display text-[13px] text-[var(--color-text-primary)]"
            decorative
          />
          <span className="text-[9px] text-[var(--color-text-subtle)] tracking-[0.06em] uppercase mt-0.5">
            We Are 172X
          </span>
        </div>
      )}
    </div>
  );
}

/* Favicon / head icon: 32×32 SVG data URL string for use in link tag */
export const faviconSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'><rect width='32' height='32' rx='6' fill='%231a3a5c'/><text x='50%' y='52%' text-anchor='middle' dominant-baseline='central' fill='white' font-family='Inter,system-ui,sans-serif' font-size='12' font-weight='800' letter-spacing='-0.04em'>172</text></svg>`;
